import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/api';
import { Draft, Team } from '../types';
import { Card, Container, LoadingSpinner, colors, media, spacing, typography } from '../styles/GlobalStyles';

const ResultsContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: ${colors.black};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  flex-wrap: wrap;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};
`;

const Title = styled.h1`
  color: ${colors.white};
  font-size: ${typography.fontSize['3xl']};
  margin: ${spacing[2]} 0 ${spacing[1]};
`;

const Subtitle = styled.p`
  color: ${colors.grey[400]};
  margin: 0;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[3]};
  margin-bottom: ${spacing[6]};

  ${media.tablet} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SummaryItem = styled.div`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[700]};
  border-radius: 0.375rem;
  background: ${colors.grey[950]};
`;

const SummaryLabel = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
`;

const SummaryValue = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  margin-top: ${spacing[1]};
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[4]};

  ${media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const TeamCard = styled(Card)`
  padding: ${spacing[4]};
`;

const TeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding-bottom: ${spacing[3]};
  border-bottom: 1px solid ${colors.grey[800]};
`;

const TeamName = styled.h2`
  color: ${colors.white};
  font-size: ${typography.fontSize.lg};
  margin: 0;
`;

const TeamMeta = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  margin-top: ${spacing[1]};
`;

const Spend = styled.div`
  color: ${colors.green};
  font-weight: ${typography.fontWeight.bold};
  white-space: nowrap;
`;

const Roster = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlayerRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing[3]};
  padding: ${spacing[3]} 0;
  border-bottom: 1px solid ${colors.grey[800]};

  &:last-child {
    border-bottom: 0;
  }
`;

const PlayerName = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlayerDetails = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
  margin-top: ${spacing[1]};
`;

const PlayerCost = styled.div`
  color: ${colors.grey[300]};
  font-weight: ${typography.fontWeight.semibold};
`;

const EmptyRoster = styled.div`
  padding: ${spacing[5]} 0;
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  text-align: center;
`;

const EmptyState = styled(Card)`
  padding: ${spacing[8]};
  color: ${colors.grey[400]};
  text-align: center;
`;

const DraftResultsPage: React.FC = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDraft = async () => {
      if (!draftId) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.drafts.getById(draftId);
        if (response.success) {
          setDraft(response.data.draft);
        }
      } catch (error) {
        console.error('Failed to load completed draft:', error);
        toast.error('Unable to load draft results');
      } finally {
        setLoading(false);
      }
    };

    loadDraft();
  }, [draftId]);

  if (loading) {
    return <ResultsContainer><Container><LoadingSpinner size="lg" /></Container></ResultsContainer>;
  }

  if (!draft || draft.status !== 'completed') {
    return (
      <ResultsContainer>
        <Container>
          <EmptyState><Trophy size={40} /><p>This draft does not have completed results to review.</p><BackLink to="/dashboard"><ArrowLeft size={16} /> Back to Dashboard</BackLink></EmptyState>
        </Container>
      </ResultsContainer>
    );
  }

  const totalSpend = draft.teams.reduce((total, team) => total + team.budget.spent, 0);
  const totalPlayers = draft.teams.reduce((total, team) => total + team.players.length, 0);

  return (
    <ResultsContainer>
      <Container>
        <Header>
          <div>
            <BackLink to="/dashboard"><ArrowLeft size={16} /> Back to Dashboard</BackLink>
            <Title>{draft.name}</Title>
            <Subtitle>Completed draft results and team rosters</Subtitle>
          </div>
        </Header>

        <Summary>
          <SummaryItem><SummaryLabel>Teams</SummaryLabel><SummaryValue>{draft.teams.length}</SummaryValue></SummaryItem>
          <SummaryItem><SummaryLabel>Players Drafted</SummaryLabel><SummaryValue>{totalPlayers}</SummaryValue></SummaryItem>
          <SummaryItem><SummaryLabel>Total Spend</SummaryLabel><SummaryValue>${totalSpend}</SummaryValue></SummaryItem>
        </Summary>

        <TeamGrid>
          {draft.teams.map((team: Team) => (
            <TeamCard key={team._id}>
              <TeamHeader>
                <div>
                  <TeamName>{team.name}</TeamName>
                  <TeamMeta>{team.players.length} players · ${team.budget.remaining} remaining</TeamMeta>
                </div>
                <Spend>${team.budget.spent}</Spend>
              </TeamHeader>
              <Roster>
                {team.players.length === 0 ? (
                  <EmptyRoster>No players drafted</EmptyRoster>
                ) : (
                  team.players.map((player) => (
                    <PlayerRow key={player.playerId}>
                      <div><PlayerName>{player.playerName}</PlayerName><PlayerDetails>{player.position} · {player.team} · Bye {player.byeWeek ?? '--'}</PlayerDetails></div>
                      <PlayerCost>${player.cost}</PlayerCost>
                    </PlayerRow>
                  ))
                )}
              </Roster>
            </TeamCard>
          ))}
        </TeamGrid>
      </Container>
    </ResultsContainer>
  );
};

export default DraftResultsPage;